import React, { useState, useEffect, useRef } from 'react';
import { usePlayerWs } from './wsplayer/usePlayerWs';
import { serverStopText } from './wsplayer/PlayerWS';
import CameraPlaceholder from './CameraPlaceholder';
import CameraInfoOverlay from './CameraInfoOverlay';
import { getOverlayPrefs, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { downloadScreenshot } from '../util/screenshotHelper';
import { debugLog } from '../util/debug';

const CameraStreamContainerWs = ({
  wsUrl,
  camName,
  cameraId,
  enabled = true,
  profile = 1, // 1 = single/few cameras (auto-detect), 2 = multi-view (force hardware)
  slotIndex,
  onObjectDetection,
  onConnectionChange,
  onContextMenu,
  onError,
  onMetadataUpdate,
  onDoubleClick,
  onFallbackNeeded,
  onFirstFrame, // Called when first frame is rendered - for smooth transitions
  onConnecting, // Cloud: called on mount/reconnect so the parent re-arms this tile's loading spinner
  showOverlay = true, // local/eView hybrid tile passes false (parent VideoViewWs renders ONE OSD); cloud single-player keeps its own (default true)
  noJpegBase = false, // cloud: no JPEG layer underneath — show status overlays immediately, no opacity fade
  deviceOnline, // cloud: device reachability from the cloud API
  onServerStop, // cloud: the server stopped this stream ({ code, message }) — e.g. access removed
}) => {
  // Handle context menu (right-click)
  const handleContextMenu = (e) => {
    e.preventDefault();
    debugLog('[CameraStreamContainerWs] Right-click detected, slotIndex:', slotIndex);
    if (onContextMenu && slotIndex !== undefined) {
      onContextMenu(e, slotIndex, { bID: cameraId, sName: camName, fEnable: enabled });
    }
  };
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  // The server's stop code while 'failed' (PlayerWS._serverStop): the overlay says why in plain words.
  const [stopCode, setStopCode] = useState(null);
  const [cameraInfo, setCameraInfo] = useState(null);
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  // Reveal the WS canvas (drop the JPEG mask) only once the decoder has SETTLED — either PlayerWS
  // reports settled (onSettled: pumping check done, hw/sw chosen), or a safety timeout elapses so a
  // slow/short stream still reveals. This hides the ~2s hardware-decode pump window behind the JPEG
  // instead of showing it on the canvas. Known resolutions settle on frame 1 -> no added delay.
  // (#86bbtgfh0)
  const [decodeSettled, setDecodeSettled] = useState(false);
  const [revealTimedOut, setRevealTimedOut] = useState(false);
  const revealTimer = useRef(null);
  const hasStalled = useRef(false);
  const stallClearTimer = useRef(null);
  // Camera Metadata overlay (#86bb5dx62 L-D3): the gear's "Camera Metadata" toggle drives
  // overlayPrefs.metadata; when on we poll the decoder's live FPS/bitrate/codec/resolution.
  const [metadataOn, setMetadataOn] = useState(() => getOverlayPrefs().metadata);
  const [stats, setStats] = useState(null);
  const isMounted = useRef(true);
  const decodeErrorCount = useRef(0);
  const framesReceived = useRef(false);
  const decodeCheckTimer = useRef(null);
  const firstFrameNotified = useRef(false);
  // Hold the LATEST onConnecting so the reset effect can call it without depending on its (inline,
  // per-render) identity — otherwise exhaustive-deps forces it into [wsUrl, cameraId] and the stream
  // would reset every render. (#86bbm58f7)
  const onConnectingRef = useRef(onConnecting);
  onConnectingRef.current = onConnecting;

  useEffect(() => {
    isMounted.current = true;
    decodeErrorCount.current = 0;
    framesReceived.current = false;
    firstFrameNotified.current = false;
    hasStalled.current = false;
    clearTimeout(stallClearTimer.current);
    stallClearTimer.current = null;
    setConnectionStatus('connecting');
    setStopCode(null);
    setCameraInfo(null);
    setHasFirstFrame(false);
    setDecodeSettled(false);
    setRevealTimedOut(false);
    clearTimeout(revealTimer.current);
    revealTimer.current = null;
    // Re-arm the parent's per-tile loading spinner on a mount/reconnect ([wsUrl, cameraId] change):
    // we're no longer painting the previous frames, so clear the parent's preserved wsStreaming[key]
    // and let onFirstFrame re-set it. Without this the spinner shows only on the FIRST view (empty map)
    // and stays suppressed on later view/grid switches by a stale `true`. (#86bbm58f7)
    if (onConnectingRef.current) onConnectingRef.current(cameraId);

    return () => {
      isMounted.current = false;
      if (decodeCheckTimer.current) {
        clearTimeout(decodeCheckTimer.current);
      }
      clearTimeout(stallClearTimer.current);
    }
  }, [wsUrl, cameraId]);

  const { canvasRef, getStats } = usePlayerWs(
    enabled ? wsUrl : null,
    {
      profile, // Pass profile for decoder strategy selection
      onObjectDetection,
      onConnectionChange: (status, metadata) => {
        if (!isMounted.current) return;
        // A 'failed' status carries the server's stop reason, not stream metadata.
        if (metadata && status !== 'failed') {
          setCameraInfo(metadata);
        }
        if (onConnectionChange) {
          onConnectionChange(status, metadata);
        }

        if (status === 'stalled') {
          hasStalled.current = true;
          clearTimeout(stallClearTimer.current);
          stallClearTimer.current = null;
          setConnectionStatus('stalled');
          if (onFallbackNeeded) onFallbackNeeded(cameraId);
          if (noJpegBase && !firstFrameNotified.current && onFirstFrame) {
            firstFrameNotified.current = true;
            setHasFirstFrame(true);
            onFirstFrame(cameraId);
          }
          return;
        }

        if (status === 'failed') {
          setConnectionStatus('failed');
          setStopCode((metadata && metadata.code) || null);
          if (noJpegBase && !firstFrameNotified.current && onFirstFrame) {
            firstFrameNotified.current = true;
            setHasFirstFrame(true);
            onFirstFrame(cameraId);
          }
          return;
        }

        if (status === 'disconnected' || status === 'reconnecting') {
          if (onFallbackNeeded) onFallbackNeeded(cameraId);
          if (!hasStalled.current) setConnectionStatus(status);
          if (noJpegBase && !firstFrameNotified.current && onFirstFrame) {
            firstFrameNotified.current = true;
            setHasFirstFrame(true);
            onFirstFrame(cameraId);
          }
          return;
        }

        if (!hasStalled.current) {
          setConnectionStatus(status);
        }

        // When connected, start a timer to check for frames
        // If no frames after 5 seconds, this camera likely needs JPEG fallback
        if (status === 'connected') {
          if (decodeCheckTimer.current) {
            clearTimeout(decodeCheckTimer.current);
          }
          decodeCheckTimer.current = setTimeout(() => {
            if (isMounted.current && !framesReceived.current && onFallbackNeeded) {
              onFallbackNeeded(cameraId);
            }
          }, 5000);
        }
      },
      onServerStop: (stop) => {
        if (isMounted.current && onServerStop) onServerStop(stop);
      },
      onError: (error) => {
        if (!isMounted.current) return;
        if (onError) {
          onError(error);
        }
        // Track decode errors - if too many, trigger fallback
        decodeErrorCount.current++;
        if (decodeErrorCount.current >= 3 && onFallbackNeeded) {
          onFallbackNeeded(cameraId);
        }
      },
      onMetadataUpdate: (metadata) => {
        if (!isMounted.current) return;
        setCameraInfo(metadata);
        if (onMetadataUpdate) {
          onMetadataUpdate(metadata);
        }
      },
      onFrame: () => {
        framesReceived.current = true;
        if (decodeCheckTimer.current) {
          clearTimeout(decodeCheckTimer.current);
          decodeCheckTimer.current = null;
        }
        if (!firstFrameNotified.current) {
          firstFrameNotified.current = true;
          setHasFirstFrame(true);
          if (onFirstFrame) {
            onFirstFrame(cameraId);
          }
        }
        if (hasStalled.current && !stallClearTimer.current) {
          stallClearTimer.current = setTimeout(() => {
            if (isMounted.current && hasStalled.current) {
              hasStalled.current = false;
              stallClearTimer.current = null;
              setConnectionStatus('connected');
            }
          }, 3000);
        }
      },
      onSettled: () => {
        // Decoder settled (pumping check done, hw/sw chosen) -> reveal the WS canvas over the JPEG.
        if (isMounted.current) setDecodeSettled(true);
      }
    }
  );

  // Live-toggle the metadata overlay from the shared prefs (the desktop gear / any subscriber).
  useEffect(() => {
    const unsub = subscribeOverlayPrefs((p) => setMetadataOn(p.metadata));
    return unsub;
  }, []);

  // Safety net for the JPEG-hold (#86bbtgfh0): if PlayerWS never reports settled (a very slow or
  // short stream that never reaches the pumping check), reveal the canvas after a bounded wait once
  // the first frame arrived, so the tile can't sit on the JPEG forever.
  useEffect(() => {
    if (!hasFirstFrame || decodeSettled || revealTimedOut) return undefined;
    revealTimer.current = setTimeout(() => {
      if (isMounted.current) setRevealTimedOut(true);
    }, 2500);
    return () => { clearTimeout(revealTimer.current); revealTimer.current = null; };
  }, [hasFirstFrame, decodeSettled, revealTimedOut]);

  // Poll the decoder ~1s for FPS/bitrate/codec/resolution, ONLY while the overlay is on and a
  // frame is flowing. getStats() resets its window each call → a live 1-second average. Off by
  // default, so there's zero polling cost in normal viewing.
  useEffect(() => {
    if (!metadataOn || !enabled || !hasFirstFrame) { setStats(null); return; }
    getStats(); // prime: discard the since-connect burst so the first sample is a clean 1s rate
    const id = setInterval(() => { if (isMounted.current) setStats(getStats()); }, 1000);
    return () => clearInterval(id);
  }, [metadataOn, enabled, hasFirstFrame, getStats]);

  // Snapshot-all gear action (#86bb5dx62): each visible WS tile captures its own canvas frame
  // (reuses screenshotHelper — same PNG + filename as the per-tile snapshot). A single stable
  // listener; current cam/time read via a ref so it never re-subscribes on each metadata tick.
  const snapRef = useRef({});
  snapRef.current = { camName, time: cameraInfo && cameraInfo.time, hasFirstFrame, enabled };
  useEffect(() => {
    const onSnapAll = () => {
      const s = snapRef.current;
      if (s.enabled && s.hasFirstFrame && canvasRef.current) {
        downloadScreenshot(canvasRef.current, { camName: s.camName || '', timestamp: s.time ? (s.time.split(' ')[1] || s.time) : '' });
      }
    };
    window.addEventListener('dview-snapshot-all-tiles', onSnapAll);
    return () => window.removeEventListener('dview-snapshot-all-tiles', onSnapAll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canvasStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: (noJpegBase && !hasFirstFrame) ? 'transparent' : '#000',
    border: 'none',
    display: 'block',
  };

  const getOverlayText = () => {
    if (noJpegBase) {
      if (connectionStatus === 'stalled') return 'Connection Stalled';
      if (connectionStatus === 'failed') return serverStopText(stopCode) || 'Connection Failed';
      return null;
    }
    switch (connectionStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return null;
      case 'disconnected':
        return 'Disconnected';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'stalled':
        return 'Connection Stalled';
      case 'failed':
        return serverStopText(stopCode) || 'Connection Failed';
      case 'stopped':
        return 'Stopped';
      default:
        return connectionStatus;
    }
  };

  const timeStamp = cameraInfo?.time?.split(" ")[1];
  const overlayText = getOverlayText();
  // Reveal the WS canvas (drop the JPEG mask) only once the decode has settled or the safety timeout
  // fired — NOT on the first frame, so the hardware-decode pump window stays hidden. (#86bbtgfh0)
  const revealWs = decodeSettled || revealTimedOut;

  // Show placeholder for disabled cameras
  if (!enabled) {
    return (
      <div
        className={`ip-camera-container`}
        style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
      >
        <CameraPlaceholder />
      </div>
    );
  }

  // WS component renders on top of JPEG with absolute positioning
  // Invisible until first frame arrives, then fades in
  return (
    <div
      className={`ip-camera-container`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        opacity: noJpegBase ? 1 : (revealWs ? 1 : 0),
        pointerEvents: (noJpegBase || revealWs) ? 'auto' : 'none',
        zIndex: 1,
      }}
      onDoubleClick={onDoubleClick}
      onContextMenu={handleContextMenu}
    >
      {noJpegBase && !hasFirstFrame && <CameraPlaceholder />}
      <canvas ref={canvasRef} style={canvasStyle} data-cam-id={cameraId} />

      {/* WS transport badge (#86bb5dx62) — present ONLY on a cleanly-streaming WebSocket tile,
          so its presence = WS and its absence = JPEG (David). Top-left (below the timestamp on
          mobile, at the top on the desktop OSD); shown on all breakpoints (Q4). Hidden while the
          metadata pill is up (it already shows the codec = WS). */}
      {!noJpegBase && hasFirstFrame && !overlayText && !metadataOn && (
        <span className="live-ws-badge" title="WebSocket stream (H.264/H.265)">WS</span>
      )}

      {(hasFirstFrame || noJpegBase) && overlayText && (
        <div style={styles.overlayText}>
          {overlayText}
        </div>
      )}

      {(hasFirstFrame || noJpegBase) && showOverlay && (
        <CameraInfoOverlay
          camName={camName}
          timestamp={timeStamp}
          streamType="ws"
        />
      )}

      {(hasFirstFrame || noJpegBase) && metadataOn && stats && (
        <div className="live-metadata-overlay">
          <span><span className="live-metadata-overlay__ws">WS</span> · {stats.width}&times;{stats.height}{stats.codec ? ` · ${stats.codec}` : ''}</span>
          <span>{stats.fps} fps · {stats.kbps >= 1000 ? `${(stats.kbps / 1000).toFixed(1)} Mbps` : `${stats.kbps} kbps`}</span>
        </div>
      )}
    </div>
  );
};

export default CameraStreamContainerWs;

const styles = {
  overlayText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(0,0,0,0.6)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'system-ui, sans-serif',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    zIndex: 2,
  },
  // Camera Metadata debug overlay (#86bb5dx62) — small mono pill, bottom-right.
  metaOverlay: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    padding: '3px 7px',
    borderRadius: '4px',
    fontSize: '10px',
    lineHeight: 1.35,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fontVariantNumeric: 'tabular-nums',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'right',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    zIndex: 6,
  },
  // COMMENTED OUT FOR PRODUCTION - Re-enable with control buttons above
  // controlButtonsContainer: {
  //   position: 'absolute',
  //   top: 8,
  //   right: 8,
  //   display: 'flex',
  //   gap: '4px',
  //   opacity: 0.7,
  //   zIndex: 10
  // },
  // controlButton: {
  //   background: 'rgba(0,0,0,0.8)',
  //   color: 'white',
  //   border: '1px solid #444',
  //   borderRadius: '4px',
  //   padding: '4px 8px',
  //   fontSize: '11px',
  //   cursor: 'pointer'
  // }
}
